import { alpha } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { typography } from '@mui/system';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CloseIcon } from 'assets/svg';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';

interface DialPadInputProps {
  dialedNumber: string;
  onClear?: () => void;
}

const MAX_DISPLAY_DIGITS = 12;

export const DialPadInput: FC<DialPadInputProps> = ({
  dialedNumber,
  onClear,
}) => {
  const { t } = useTranslation('translation');
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollLeft = ref.current.scrollWidth;
    }
  }, [dialedNumber]);

  // Truncate value if longer than MAX_DISPLAY_DIGITS, show ... and last N digits
  const displayValue =
    dialedNumber.length > MAX_DISPLAY_DIGITS
      ? `...${dialedNumber.slice(-MAX_DISPLAY_DIGITS)}`
      : dialedNumber;

  return (
    <InputWrapper data-testid='dialpad-input-wrapper'>
      <Container
        ref={ref}
        type='text'
        disabled
        value={displayValue}
        placeholder={dialedNumber ? '' : t('dialPad.enterNumber')}
      />
      {dialedNumber && onClear && (
        <Tooltip title={t('dialPad.clearBtn')} placement='top'>
          <span>
            <ClearButton
              onClick={onClear}
              data-testid='dialpad-input-clear-button'
            >
              <CloseIcon />
            </ClearButton>
          </span>
        </Tooltip>
      )}
    </InputWrapper>
  );
};

const InputWrapper = styled('div')`
  position: relative;
  width: 100%;
`;

const Container = styled('input')`
  width: 100%;
  height: 68px;
  padding: ${theme.spacing(3)} ${theme.spacing(14)};
  text-align: center;
  border: none;
  outline: none;
  caret-color: transparent;
  color: ${theme.palette.common.white};
  border-radius: 12px 12px 0 0;
  background-color: #2f3438;
  border-bottom: solid 0.5px ${alpha(theme.palette.common.white, 0.1)};
  ${typography(theme.typography.dialPadInput)};
  letter-spacing: 1px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.34);
    text-align: center;
    font-family: Inter;
    font-size: 24px;
    font-style: normal;
    font-weight: 500;
    line-height: 28px; /* 116.667% */
  }

  &:placeholder-shown {
    text-align: center;
  }
`;

const ClearButton = styled('button')`
  position: absolute;
  right: ${theme.spacing(2)};
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  background: none;
  cursor: pointer;
  color: ${alpha(theme.palette.common.white, 0.7)};

  &:hover {
    color: ${theme.palette.common.white};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;
